import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, createVNode as _createVNode, unref as _unref, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 0,
  class: "item-card-pill",
  "data-testid": "item-card-pill"
}
const _hoisted_4 = ["role", "tabindex", "aria-label", "onKeydown"]
const _hoisted_5 = { class: "item-card-header-right" }
const _hoisted_6 = {
  key: 0,
  class: "item-card-header-statuses"
}
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = {
  key: 1,
  class: "item-card-header-action-menu"
}
const _hoisted_9 = {
  key: 1,
  class: "item-card-content",
  "data-testid": "item-card-content"
}

import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import LazyImage from '@shell/components/LazyImage.vue';
import { DropdownOption } from '@components/RcDropdown/types';
import ActionMenu from '@shell/components/ActionMenuShell.vue';
import RcItemCardAction from './RcItemCardAction';
import RcIcon from '@components/RcIcon/RcIcon.vue';
import { RcIconType } from '@components/RcIcon/types';

type RcItemCardVariant = 'small' | 'medium';

/**
 * A label that can be either plain text or a translatable key.
 */
type Label = {
  key?: string;
  text?: string;
};

/**
 * Represents an image used in the card.
 * Can be either a traditional image (src) or an icon (icon).
 */
type Image =
 | { src?: never, icon: RcIconType; alt: Label; }
 | { src: string; icon?: never, alt: Label; };

/**
 * Optional pill badge, typically used to highlight a tag or state.
 */
type Pill = {
  label: Label;
  tooltip?: Label;
};

/**
 * Represents an icon-based status indicator shown in the card header.
 */
type Status = {
  icon: string;
  color?: string;
  customColor?: string;
  tooltip?: Label;
  handleClick?: () => void;
};

/**
 * Header metadata for the card.
 */
type Header = {
  title?: Label;
  statuses?: Status[];
};

/**
 * The generic data value passed to the card.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ItemValue = Record<string, any>;

/**
 * Props accepted by the ItemCard component.
 */
interface RcItemCardProps {
  /** Unique identifier for the card (used in test IDs) */
  id: string;

  /** Any object value associated with this card */
  value?: ItemValue;

  /** Card title, status icons and action menu. Image will be included in the header in small variant too */
  header: Header;

  /** Optional image to show in card (position depends on variant). A slot is available for it too #item-card-image */
  image?: Image;

  /** Optional actions that will be displayed inside an action-menu
   *
   * Each action should include an `action` name, which is included in the payload
   * of the 'action-invoked' event when the action is selected.
   *
   * Example:
   * <rc-item-card
   *   :actions="[
   *     {
   *       action: 'focusSearch',
   *       label: t('catalog.charts.search'),
   *       enabled: true
   *     }
   *   ]"
   *   @action-invoked="handleActionInvoked"
   * />
   *
   * The handler receives a payload with { action: string, actionData: object, event: MouseEvent, ... }
   */
  actions?: DropdownOption[];

  /** Text content inside the card body. A slot is available for it too #item-card-content */
  content?: Label;

  /** Layout variant: 'small' or 'medium' */
  variant?: RcItemCardVariant;

  /** Optional pill shown (only if variant is not 'small'). A slot is available for it too #item-card-pill */
  pill?: Pill;

  /** Makes the card clickable and emits 'card-click' on click/enter/space */
  clickable?: boolean;

  /** The card will have same style as hover clickable with the blue border when selected */
  selected?: boolean;

  /** Disables the card, preventing clicks and keyboard interaction */
  disabled?: boolean;

  role?: 'link' | 'button' | undefined;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RcItemCard',
  props: {
    id: {},
    value: {},
    header: {},
    image: {},
    actions: {},
    content: {},
    variant: {},
    pill: {},
    clickable: { type: Boolean },
    selected: { type: Boolean },
    disabled: { type: Boolean },
    role: {}
  },
  emits: ["card-click", "action-invoked"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "v3d32a064": (cursorValue.value)
}))

const store = useStore();
const { t } = useI18n(store);

/**
 * Variants available for ItemCard layout
 */
const props = __props;

/**
 * Emits:
 * - 'card-click' when card is clicked or activated via keyboard.
 * - 'action-invoked' when an action is selected from the action menu (only when `actions` prop is used).
 */
const emit = __emit;

/**
 * Handles the card click while avoiding nested interactive elements
 * By using RcItemCardAction.vue the 'item-card-action' attribute automatically gets added
 * which then gets used to ignore 'card-click'
 */
function _handleCardClick(e: MouseEvent | KeyboardEvent) {
  if (props.disabled) {
    return;
  }

  const interactiveSelector = '[item-card-action]';

  // Prevent card click if the user clicks on an inner actionable element like repo, category, or tag
  if ((e.target as HTMLElement).closest(interactiveSelector)) {
    return;
  }

  emit('card-click', props.value);
}

/**
 * Utility to resolve localized or plain text labels.
 */
function labelText(label?: Label): string {
  return label?.key ? t(label.key) : label?.text ?? '';
}

/** ---------------- data ------------------ */
const cardEl = ref<HTMLElement | null>(null);

/** ---------------- Computed ------------------ */

const headerTitle = computed(() => labelText(props.header.title));
const imageAlt = computed(() => labelText(props.image?.alt));
const pillLabel = computed(() => labelText(props.pill?.label));
const pillTooltip = computed(() => labelText(props.pill?.tooltip));
const contentText = computed(() => labelText(props.content));
const statusTooltips = computed(() => props.header.statuses?.map((status) => labelText(status.tooltip)) || []);

const cardMeta = computed(() => ({
  ariaLabel:       props.clickable ? t('itemCard.ariaLabel.clickable', { cardTitle: labelText(props.header.title) }) : undefined,
  tabIndex:        props.clickable && !props.disabled ? '0' : undefined,
  role:            props.role ?? (props.clickable && !props.disabled ? 'button' : undefined),
  actionMenuLabel: props.actions && t('itemCard.actionMenu.label', { cardTitle: labelText(props.header.title) }),
}));

const cursorValue = computed(() => props.clickable ? 'pointer' : 'auto');

return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "cardEl",
    ref: cardEl,
    class: _normalizeClass(["item-card", {
      'clickable':
        __props.clickable && !__props.disabled,'selected': __props.selected, 'disabled': __props.disabled
    }]),
    "data-testid": `item-card-${__props.id}`,
    onClick: _handleCardClick
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(['item-card-body', __props.variant])
    }, [
      (__props.variant !== 'small')
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _renderSlot(_ctx.$slots, "item-card-image", {}, () => [
              (__props.image)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(['item-card-image', __props.variant]),
                    "data-testid": "item-card-image"
                  }, [
                    (__props.image.icon)
                      ? (_openBlock(), _createBlock(RcIcon, {
                          key: 0,
                          type: __props.image.icon,
                          size: "xxlarge"
                        }, null, 8, ["type"]))
                      : (__props.image.src)
                        ? (_openBlock(), _createBlock(LazyImage, {
                            key: 1,
                            src: __props.image.src,
                            alt: imageAlt.value,
                            style: {'width': '40px', 'height': '40px', 'object-fit': 'contain'}
                          }, null, 8, ["src", "alt"]))
                        : _createCommentVNode("", true)
                  ], 2))
                : _createCommentVNode("", true)
            ]),
            _cache[1] || (_cache[1] = _createTextVNode()),
            _renderSlot(_ctx.$slots, "item-card-pill", {}, () => [
              (__props.pill)
                ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createTextVNode(_toDisplayString(pillLabel.value), 1)
                  ])), [
                    [_directive_clean_tooltip, pillTooltip.value]
                  ])
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      _cache[10] || (_cache[10] = _createTextVNode()),
      _createElementVNode("div", {
        class: _normalizeClass(['item-card-body-details', __props.variant])
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(['item-card-header', __props.variant])
        }, [
          _createElementVNode("div", {
            class: "item-card-header-left",
            "data-testid": `card-header-left`,
            role: cardMeta.value.role,
            tabindex: cardMeta.value.tabIndex,
            "aria-label": cardMeta.value.ariaLabel,
            onClick: _withModifiers(_handleCardClick, ["self"]),
            onKeydown: [
              _withKeys(_handleCardClick, ["enter"]),
              _withKeys(_withModifiers(_handleCardClick, ["prevent"]), ["space"])
            ]
          }, [
            (__props.variant === 'small')
              ? _renderSlot(_ctx.$slots, "item-card-image", { key: 0 }, () => [
                  (__props.image)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass(['item-card-image', __props.variant]),
                        "data-testid": "item-card-image"
                      }, [
                        (__props.image.icon)
                          ? (_openBlock(), _createBlock(RcIcon, {
                              key: 0,
                              type: __props.image.icon,
                              size: "xlarge"
                            }, null, 8, ["type"]))
                          : (__props.image.src)
                            ? (_openBlock(), _createBlock(LazyImage, {
                                key: 1,
                                src: __props.image.src,
                                alt: imageAlt.value,
                                style: {'width': '32px', 'height': '32px', 'object-fit': 'contain'}
                              }, null, 8, ["src", "alt"]))
                            : _createCommentVNode("", true)
                      ], 2))
                    : _createCommentVNode("", true)
                ])
              : _createCommentVNode("", true),
            _cache[2] || (_cache[2] = _createTextVNode()),
            _renderSlot(_ctx.$slots, "item-card-header-title", {}, () => [
              (__props.header.title)
                ? _withDirectives((_openBlock(), _createElementBlock("h3", {
                    key: 0,
                    class: _normalizeClass(['item-card-header-title', __props.variant]),
                    "data-testid": "item-card-header-title"
                  }, [
                    _createTextVNode(_toDisplayString(headerTitle.value), 1)
                  ], 2)), [
                    [_directive_clean_tooltip, headerTitle.value]
                  ])
                : _createCommentVNode("", true)
            ])
          ], 40, _hoisted_4),
          _cache[4] || (_cache[4] = _createTextVNode()),
          _createElementVNode("div", _hoisted_5, [
            (__props.header.statuses?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.header.statuses, (status, i) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: i,
                      class: "item-card-header-statuses-status",
                      "data-testid": "item-card-header-statuses-status"
                    }, [
                      _withDirectives(_createElementVNode("i", {
                        class: _normalizeClass(['icon', status.icon, status.color]),
                        style: _normalizeStyle({color: status.customColor}),
                        "data-testid": `item-card-header-status-${i}`
                      }, null, 14, _hoisted_7), [
                        [_directive_clean_tooltip, statusTooltips.value[i]]
                      ])
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _cache[3] || (_cache[3] = _createTextVNode()),
            (_ctx.$slots['item-card-actions'])
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _renderSlot(_ctx.$slots, "item-card-actions")
                ]))
              : (__props.actions)
                ? (_openBlock(), _createBlock(_unref(RcItemCardAction), {
                    key: 2,
                    class: "item-card-header-action-menu"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(ActionMenu, {
                        "data-testid": "item-card-header-action-menu",
                        "button-aria-label": cardMeta.value.actionMenuLabel,
                        "custom-actions": __props.actions,
                        onActionInvoked: _cache[0] || (_cache[0] = (payload) => emit('action-invoked', payload))
                      }, null, 8, ["button-aria-label", "custom-actions"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
          ])
        ], 2),
        _cache[7] || (_cache[7] = _createTextVNode()),
        _renderSlot(_ctx.$slots, "item-card-sub-header", {}, () => [
          _cache[5] || (_cache[5] = _createElementVNode("div", null, null, -1))
        ]),
        _cache[8] || (_cache[8] = _createTextVNode()),
        (_ctx.$slots['item-card-content'])
          ? _renderSlot(_ctx.$slots, "item-card-content", { key: 0 }, () => [
              _cache[6] || (_cache[6] = _createElementVNode("div", {
                class: "item-card-content",
                "data-testid": "item-card-content"
              }, null, -1))
            ])
          : (__props.content)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("p", null, _toDisplayString(contentText.value), 1)
              ]))
            : _createCommentVNode("", true),
        _cache[9] || (_cache[9] = _createTextVNode()),
        _renderSlot(_ctx.$slots, "item-card-footer")
      ], 2)
    ], 2)
  ], 10, _hoisted_1))
}
}

})